/**
 * Generic API response wrapper interface that matches the backend ApiResponse<T> structure
 * Provides consistent response format across all API endpoints
 */
export interface ApiResponse<T = any> {
    /**
     * Indicates whether the operation was successful
     */
    success: boolean;

    /**
     * Error message if the operation failed, null if successful
     */
    error: string | null;

    /**
     * Additional message or information about the operation
     */
    message: string | null;

    /**
     * The actual data payload of the response
     */
    data: T | null;

    /**
     * HTTP status code for the response
     */
    statusCode: number;

    /**
     * Timestamp when the response was generated
     */
    timestamp: string;
}

/**
 * Type guard to check if a response is successful
 */
export function isSuccessResponse<T>(response: ApiResponse<T>): response is ApiResponse<T> & { success: true; data: T } {
    return response.success === true || response.data !== null;
}

/**
 * Type guard to check if a response is an error
 */
export function isErrorResponse<T>(response: ApiResponse<T>): response is ApiResponse<T> & { success: false; error: string } {
    return response.success === false && response.error !== null;
}

/**
 * Helper function to extract data from API response safely
 */
export function extractData<T>(response: ApiResponse<T>): T | null {
    return isSuccessResponse(response) ? response.data : null;
}

/**
 * Helper function to extract error message from API response
 */
export function extractError<T>(response: ApiResponse<T>): string {
    return isErrorResponse(response) ? response.error : 'Unknown error occurred';
}

/**
 * Helper function to extract user-friendly message from API response
 */
export function extractMessage<T>(response: ApiResponse<T>): string {
    return response.message || (response.success ? 'Operation completed successfully' : 'An error occurred');
}